<template>
	<div class="container">
		<div class="content">
			<div v-if="mode === 'list'" class="list_container">
				<h3 class="title mb_20">{{ $t('rtr_activity-exchange.title1') }}</h3>
				<div class="d_flex align_items_end mb_20">
					<div class="info mr_10">
						<p class="mb_10">{{ $t('rtr_activity.start') }}</p>
						<date-picker
							is-required
							v-model="startDate"
							:model-config="{ type: 'string' }"
							:locale="$store.state.common.locale"
						>
							<template v-slot="{ inputValue, inputEvents }">
								<label class="calendar_area">
									<input
										class="px-2 py-1 border rounded focus:outline-none focus:border-blue-300"
										:value="inputValue"
										v-on="inputEvents"
									/>
								</label>
							</template>
						</date-picker>
					</div>
					<div class="info mr_10">
						<p class="mb_10">{{ $t('rtr_activity.end') }}</p>
						<date-picker
							is-required
							v-model="endDate"
							:model-config="{ type: 'string' }"
							:locale="$store.state.common.locale"
						>
							<template v-slot="{ inputValue, inputEvents }">
								<label class="calendar_area">
									<input
										class="px-2 py-1 border rounded focus:outline-none focus:border-blue-300"
										:value="inputValue"
										v-on="inputEvents"
									/>
								</label>
							</template>
						</date-picker>
					</div>
					<div class="select_area mr_10">
						<select
							v-model="assetSeq"
							class="asset_area"
							@change="loadDirectTransferList('pageInit')"
						>
							<option :value="null">
								{{ $t('input.placeholder.all_assets') }}
							</option>
							<option
								v-for="item in accounts"
								:key="item.seq"
								:value="item.asset_seq"
								:label="item.asset_title"
							/>
						</select>
						<span class="select_icon"></span>
					</div>
				</div>
				<ul class="tab_area mb_20">
					<li
						:class="{ selected: selectedTab === 'ALL' }"
						@click="selectedTab = 'ALL'"
					>
						<a>{{ $t('rtr_activity-exchange.tab1') }}</a>
					</li>
					<li
						:class="{ selected: selectedTab === 'BID' }"
						@click="selectedTab = 'BID'"
					>
						<a>{{ $t('rtr_activity-exchange.tab2') }}</a>
					</li>
					<li
						:class="{ selected: selectedTab === 'ASK' }"
						@click="selectedTab = 'ASK'"
					>
						<a>{{ $t('rtr_activity-exchange.tab3') }}</a>
					</li>
				</ul>

				<div v-if="accountTransferList.length === 0" class="list_none">
					<span class="ERRORGRAY"></span>
					<p>{{ $t('rtr_activity-exchange.empty') }}</p>
				</div>

				<div v-else class="list">
					<table class="table_type">
						<thead>
							<tr>
								<th>{{ $t('rtr_activity-exchange.th1') }}</th>
								<th>{{ $t('rtr_activity-exchange.th2') }}</th>
								<th>{{ $t('rtr_activity-exchange.th3') }}</th>
								<th>{{ $t('rtr_activity-exchange.th4') }}</th>
								<th>{{ $t('rtr_activity-exchange.th5') }}</th>
								<th>{{ $t('rtr_activity-exchange.th6') }}</th>
								<th>{{ $t('rtr_activity-exchange.th7') }}</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="item in accountTransferList"
								:key="item.seq"
								@click="showDetail(item)"
							>
								<td v-if="item.transfer_type === 'BID'">
									<span class="label_buy">{{
										$t('rtr_activity-exchange.buy')
									}}</span>
								</td>
								<td v-else>
									<span class="label_sell">{{
										$t('rtr_activity-exchange.sell')
									}}</span>
								</td>
								<td>
									{{ $dayjs(item.regdate).format('YYYY.MM.DD HH:mm:ss') }}
								</td>
								<td>
									{{ item.source_asset_code }} <br />
									{{ item.source_amount }}
								</td>
								<td>
									{{ item.target_asset_code }} <br />
									{{ item.target_amount }}
								</td>
								<td>{{ item.basic_rate }}</td>
								<td>{{ item.spread_rate }}</td>
								<td>{{ item.exchange_rate }}</td>
							</tr>
						</tbody>
					</table>
					<pagination
						:totalCount="pageNavigation.total_count"
						:totalPage="pageNavigation.total_page"
						:curPage="pageNavigation.cur_page"
						:point="pageNavigation.point"
						:firstPage="pageNavigation.first_page"
						:lastPage="pageNavigation.last_page"
						@change="pageChangeHandler"
					></pagination>
				</div>
			</div>
			<ExchangeDetail
				v-if="mode === 'detail'"
				:exchangeInfo="exchangeInfo"
				@back="this.mode = 'list'"
			></ExchangeDetail>
		</div>
	</div>
</template>

<script>
import ExchangeDetail from '@/components/User/Activity/ExchangeDetail';
import { commonError } from '@/common/dialog';
import { mapGetters } from 'vuex';
import pagination from '@/components/Pagination';
import { fetchDirectTransfers } from '@/api/market';

export default {
	name: 'Exchange',
	components: { ExchangeDetail, pagination },
	data() {
		return {
			selectedTab: 'ALL',
			accountTransferList: [],
			startDate: this.$dayjs(new Date()).add(-1, 'month').toDate(),
			endDate: new Date(),
			assetSeq: null,
			pageNavigation: {},
			curPage: 1,
			listCount: 10,
			mode: 'list',
			exchangeInfo: {},
		};
	},
	computed: {
		...mapGetters('account', ['accounts']),
	},
	watch: {
		selectedTab() {
			this.loadDirectTransferList('pageInit');
		},
		startDate() {
			this.loadDirectTransferList('pageInit');
		},
		endDate() {
			this.loadDirectTransferList('pageInit');
		},
	},
	created() {
		this.assetSeq = this.$route.query.asset_seq || null;
		this.init();
	},
	methods: {
		init() {
			this.loadDirectTransferList();
		},
		async loadDirectTransferList(type) {
			if (type === 'pageInit') {
				this.curPage = 1;
			}
			const params = {
				source_asset_seq: this.assetSeq,
				transfer_type: this.selectedTab === 'ALL' ? null : this.selectedTab,
				regdate_start: this.$dayjs(new Date(this.startDate))
					.startOf('day')
					.valueOf(),
				regdate_end: this.$dayjs(this.endDate).endOf('day').valueOf(),
				page: this.curPage,
				list_count: this.listCount,
			};
			const { data, page_navigation } = await fetchDirectTransfers(
				params,
			).catch(commonError);
			this.accountTransferList = data;
			this.pageNavigation = page_navigation;
		},
		pageChangeHandler(pageNo) {
			this.curPage = pageNo;
			this.loadDirectTransferList();
		},
		showDetail(info) {
			this.exchangeInfo = info;
			this.mode = 'detail';
		},
	},
};
</script>
